import { useState, useContext, useEffect, useRef } from "react";
import {
  Card,
  Typography,
  Input,
  Button,
  Flex,
  message,
  ConfigProvider,
  Modal,
} from "antd";

import { ThemeContext } from "../../context/ThemeContext";


const { Title, Paragraph, Text } = Typography;

const asignacionesPredefinidas = {
  "Citlali Cecilia González Poot": "Lumen",
  "Leonardo Puc": "Lumen",
  "Cristian Manuel Sánchez Cahuich": "Lux",
  "Griselda Noemi Trujeque Ávila": "Lux",

  "Gonzalo Benjamín Rivero Barrera": "Fortis",
  "Mariana Magaña": "Flamma",
  "Karime Anilu Meza Meléndez": "Lux",
  "Brandon David Hernández Tun": "Flamma",

  "Wendy Isabel Pablo Carrillo": "Lumen",
  "Mónica Torres Uicab": "Fortis",
  "Pablo Marín": "Lumen",
  "Pedro Enrique Be Quevedo": "Fortis",

  "Enrique Mukul Cruz": "Flamma",
  "Linda Amairani Vazquez Cahuich": "Flamma",
  "Juan Almeyda": "Lux",
  "Andrea Magaña": "Fortis",
};

// Normaliza nombres para permitir diferencias de mayúsculas/minúsculas
// y acentos al momento de buscar una persona.
const normalizarNombre = (nombre) => {
  return nombre
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

// Creamos una versión del registro con las claves normalizadas.
const asignacionesNormalizadas = Object.fromEntries(
  Object.entries(asignacionesPredefinidas).map(([nombre, elemento]) => [
    normalizarNombre(nombre),
    elemento,
  ])
);


const elementosUnicos = [...new Set(Object.values(asignacionesPredefinidas))];

const NARANJA = "#F97316";
const NEGRO = "#000000";

export default function SorteoTribu() {

  const { theme } = useContext(ThemeContext);
  const [nombre, setNombre] = useState("");
  const [step, setStep] = useState("register");
  const [elemento, setElemento] = useState("");
  const [elementoVisible, setElementoVisible] = useState("");
  const [modalAbierto, setModalAbierto] = useState(true);
  const [invertido, setInvertido] = useState(false);
  const timeoutRef = useRef(null);

  const iniciarSorteo = () => {
    const inputName = normalizarNombre(nombre);

    if (!inputName) {
      message.warning("Por favor, ingresa tu nombre.");
      return;
    }

    if (!asignacionesNormalizadas[inputName]) {
      message.error(
        "No encontramos tu nombre en el registro oficial. Verifica que esté bien escrito o contacta al organizador."
      );
      return;
    }

    const elementoFinal = asignacionesNormalizadas[inputName];

    setStep("loading");

    let velocidad = 90;
    let elapsed = 0;
    const duracionTotal = 9000;

    const tick = () => {
      setElementoVisible((prev) => {
        let next;

        do {
          next =
            elementosUnicos[
            Math.floor(Math.random() * elementosUnicos.length)
            ];
        } while (next === prev && elementosUnicos.length > 1);

        return next;
      });

      setInvertido((prev) => !prev);

      elapsed += velocidad;

      if (elapsed > duracionTotal * 0.5) velocidad = 150;
      if (elapsed > duracionTotal * 0.75) velocidad = 250;
      if (elapsed > duracionTotal * 0.9) velocidad = 400;

      if (elapsed >= duracionTotal) {
        clearTimeout(timeoutRef.current);

        setElementoVisible(elementoFinal);
        setElemento(elementoFinal);
        setInvertido(false);
        setStep("result");

        return;
      }

      timeoutRef.current = setTimeout(tick, velocidad);
    };

    tick();
  };

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const isDark = theme === "dark";

  const fondoLoading = invertido ? NARANJA : NEGRO;
  const textoLoading = invertido ? NEGRO : NARANJA;

  return (
    <Flex
      justify="center"
      align="center"
      style={{ minHeight: "100vh", padding: 20 }}
    >
      <Modal
        title="⚠️ Advertencia de fotosensibilidad"
        open={modalAbierto}
        onOk={() => setModalAbierto(false)}
        okText="Entendido"
        cancelButtonProps={{ style: { display: "none" } }}
        maskClosable={false}
        keyboard={false}
      >
        La siguiente animación contiene cambios rápidos de color y efectos
        visuales que pueden desencadenar reacciones en personas con epilepsia
        fotosensible u otras afecciones similares. Si eres sensible a este tipo
        de estímulos, evita visualizar la animación o baja el brillo de tu pantalla.
      </Modal>
      <Card
        style={{
          width: "100%",
          maxWidth: 450,
          borderRadius: 16,
          textAlign: "center",
        }}
      >
        {step === "register" && (
          <>
            <Title level={2}>¡Bienvenido!</Title>

            <img
              src={
                isDark ? "img/IGNIS - Logo.png" : "img/IGNIS - Logo Negro.png"
              }
              alt="Logo de IGNIS"
              style={{ width: 120, marginBottom: 20 }}
            />

            <Paragraph type="secondary">
              Estás a punto de descubrir cuál será tu equipo en esta gran
              aventura. Ingresa tu nombre completo tal y como fuiste
              registrado.
            </Paragraph>

            <Input
              size="large"
              placeholder="Tu nombre aquí..."
              value={nombre}
              allowClear
              onChange={(e) => setNombre(e.target.value)}
              style={{ textAlign: "center", marginBottom: 20 }}
            />

            <ConfigProvider
              theme={{
                token: {
                  colorPrimary: "#F97316",
                  colorPrimaryHover: "#FB923C",
                  colorPrimaryActive: "#EA580C",
                },
              }}
            >
              <Button
                type="primary"
                size="large"
                block
                onClick={iniciarSorteo}
                color="orange"
              >
                Girar la Ruleta / Descubrir
              </Button>
            </ConfigProvider>
          </>
        )}

        {step === "loading" && (
          <>
            <Title level={2}>Girando la ruleta...</Title>

            <Paragraph type="secondary">
              Buscando tu destino entre los equipos...
            </Paragraph>

            <Card
              style={{
                marginTop: 20,
                background: fondoLoading,
                border: `2px solid ${NARANJA}`,
                borderRadius: 12,
                overflow: "hidden",
                transition: "background-color 0.08s ease",
              }}
            >
              <Title
                level={2}
                style={{
                  margin: 0,
                  color: textoLoading,
                  transition: "color 0.08s ease",
                }}
              >
                Equipo{" "}
                <span style={{ display: "inline-block", minWidth: 100 }}>
                  {elementoVisible || "..."}
                </span>
              </Title>
            </Card>
          </>
        )}

        {step === "result" && (
          <>
            <Title level={2}>
              ¡Felicidades, <Text strong>{nombre}</Text>!
            </Title>

            <Paragraph type="secondary">
              Has sido asignado oficialmente a:
            </Paragraph>

            <Card
              style={{
                marginTop: 20,
                background: NEGRO,
                border: `2px solid ${NARANJA}`,
                borderRadius: 12,
              }}
            >
              <Title level={2} style={{ margin: 0, color: NARANJA }}>
                Equipo {elemento}
              </Title>
            </Card>
          </>
        )}
      </Card>
    </Flex >
  );
}
